import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItem,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import React from "react";
import { CaregiverId } from "../../../shared/schema/schema";
import CaregiverSelect from "../../caregiver/components/CaregiverSelect";
import {
  BuilderContext,
  formatHours,
  formatWeeklyPattern,
  text,
  Verification,
} from "../ai-weekly-template-builder.utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  patientName: string;
  authorization: BuilderContext["authorization"];
  verification: Verification;
  isApplying: boolean;
  /** A server rejection. Set means the dialog stays open so it can be fixed. */
  errorMessage: string | null;
  onConfirm: (input: { caregiverId: CaregiverId | null }) => void;
}

/**
 * The confirmation step before anything is written.
 *
 * Follows the `ApprovalConfirmationDialog` pattern from billing rather than the
 * promise-style `useAskForConfirmation` hook, which only carries a title and a
 * description string and so cannot host a caregiver picker.
 */
export default function AiWeeklyTemplateApplyDialog(props: Props) {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [caregiverId, setCaregiverId] = React.useState<CaregiverId | null>(null);
  const [confirmed, setConfirmed] = React.useState(false);

  // Reset on close so reopening never inherits a half-made decision.
  React.useEffect(() => {
    if (!props.isOpen) {
      setCaregiverId(null);
      setConfirmed(false);
    }
  }, [props.isOpen]);

  const { verification, authorization } = props;
  const shiftCount = verification.weeklyShifts.length;
  const acceptedTrueUps = verification.trueUpRows.filter((row) => row.accepted);
  const warningCount = verification.violations.filter(
    (violation) => violation.severity !== "ERROR"
  ).length;

  return (
    <AlertDialog
      closeOnOverlayClick={!props.isApplying}
      isOpen={props.isOpen}
      leastDestructiveRef={cancelRef}
      size="xl"
      onClose={props.onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>
            Create weekly template and {shiftCount} shift{shiftCount === 1 ? "" : "s"}
          </AlertDialogHeader>

          <AlertDialogBody display="flex" flexDirection="column" gap={4}>
            <Text>
              For <b>{props.patientName}</b>, across authorization {text(authorization.startDate)}{" "}
              to {text(authorization.endDate)}. Visits are created from today onwards — nothing is
              backdated.
            </Text>

            <Box bg="gray.50" borderRadius="md" p={3}>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                Repeating week
              </Text>
              <Text fontSize="sm">{formatWeeklyPattern(verification.weeklyShifts)}</Text>
              <Text color="gray.600" fontSize="sm" mt={1}>
                {formatHours(verification.weeklyTotalMinutes)} per week
              </Text>
            </Box>

            <UnorderedList fontSize="sm" spacing={1}>
              <ListItem>
                {shiftCount} weekly template shift{shiftCount === 1 ? "" : "s"}
              </ListItem>
              <ListItem>
                One recurring visit per shift, on every matching day left in the authorization
              </ListItem>
              {acceptedTrueUps.length > 0 && (
                <ListItem>
                  {acceptedTrueUps.length} one-off top-up visit
                  {acceptedTrueUps.length === 1 ? "" : "s"} near the end of each period, to use the
                  remaining authorized hours
                </ListItem>
              )}
            </UnorderedList>

            <Text color="gray.600" fontSize="sm">
              Nothing is broadcast. No caregiver is notified.
            </Text>

            <FormControl>
              <FormLabel fontSize="sm">Caregiver (optional)</FormLabel>
              <CaregiverSelect
                isDisabled={props.isApplying}
                value={caregiverId}
                onChange={(caregiver) => setCaregiverId(caregiver?.id ?? null)}
              />
              <FormHelperText>Assigning now saves broadcasting these visits later.</FormHelperText>
            </FormControl>

            {caregiverId === null && (
              <Alert fontSize="sm" status="warning">
                <AlertIcon />
                <AlertDescription>
                  Shifts and visits will be created unassigned. You will need to assign or broadcast
                  them yourself.
                </AlertDescription>
              </Alert>
            )}

            {warningCount > 0 && (
              <Alert fontSize="sm" status="warning">
                <AlertIcon />
                <AlertDescription>
                  This schedule verified with {warningCount} note
                  {warningCount === 1 ? "" : "s"}. Worth reading them before creating.
                </AlertDescription>
              </Alert>
            )}

            <Checkbox
              isChecked={confirmed}
              isDisabled={props.isApplying}
              onChange={(event) => setConfirmed(event.target.checked)}
            >
              I have reviewed this schedule and want to create these visits.
            </Checkbox>

            {props.errorMessage !== null && (
              <Alert status="error">
                <AlertIcon />
                <AlertDescription>{props.errorMessage}</AlertDescription>
              </Alert>
            )}
          </AlertDialogBody>

          <AlertDialogFooter gap={3}>
            <Button
              ref={cancelRef}
              isDisabled={props.isApplying}
              variant="ghost"
              onClick={props.onClose}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isDisabled={!confirmed || props.isApplying}
              isLoading={props.isApplying}
              loadingText="Creating…"
              onClick={() => props.onConfirm({ caregiverId })}
            >
              Create
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
